// EditToolbar.tsx

import React, { useState, useRef, useEffect } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  List,
  ListOrdered,
  Undo,
  Redo,
  ChevronDown,
  Highlighter,
  Table,
  CheckSquare,
  Strikethrough,
  Palette,
  RotateCcw,
  RotateCw,
} from 'lucide-react';
import { Button } from 'components/ui/button';

interface EditToolbarProps {
  isVisible: boolean;
  onFormatClick: (format: string, value?: number | null | string) => void;
  onUndo: () => void;
  onRedo: () => void;
  wordCount: number;
  activeFormats?: { [key: string]: boolean | number | string | unknown };
}

export function EditToolbar({
  isVisible,
  onFormatClick,
  onUndo,
  onRedo,
  wordCount,
  activeFormats = {},
}: EditToolbarProps) {
  const [showHeadingDropdown, setShowHeadingDropdown] = useState(false);
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  const [showBgColorDropdown, setShowBgColorDropdown] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const colorRef = useRef<HTMLDivElement>(null);
  const bgColorRef = useRef<HTMLDivElement>(null);

  const colors = [
    { name: 'Default', value: '' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Yellow', value: '#F59E0B' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Gray', value: '#6B7280' },
  ];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowHeadingDropdown(false);
      }
      if (
        colorRef.current &&
        !colorRef.current.contains(event.target as Node)
      ) {
        setShowColorDropdown(false);
      }
      if (
        bgColorRef.current &&
        !bgColorRef.current.contains(event.target as Node)
      ) {
        setShowBgColorDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getButtonClass = (format: string) => {
    const isActive = activeFormats[format];
    return `p-2 rounded ${
      isActive ? 'bg-gray-200 text-blue-600' : 'hover:bg-gray-100'
    }`;
  };

  const getListButtonClass = (format: string, listType: string) => {
    const isActive = activeFormats.list === listType;
    return `p-2 rounded ${
      isActive ? 'bg-gray-200 text-blue-600' : 'hover:bg-gray-100'
    }`;
  };

  if (!isVisible) return null;

  return (
    <div className="relative w-full bottom-0 left-0 right-0 flex rounded-lg justify-center bg-background-main">
      <div className="w-full border-t border-primary text-primary">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="text-sm  mr-4">{wordCount} WORDS</div>
          <div className="flex items-center gap-2">
            {/* Heading Dropdown */}
            <div className="relative">
              <button
                ref={buttonRef}
                onClick={() => setShowHeadingDropdown(!showHeadingDropdown)}
                className={`flex items-center gap-1 p-2 rounded font-medium ${
                  activeFormats.header ? ' text-primary' : 'hover:bg-white'
                }`}
              >
                H<ChevronDown size={14} />
              </button>
              {showHeadingDropdown && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 bottom-full left-0 mb-1 w-40 bg-background-main rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                >
                  <div className="py-1">
                    {[1, 2, 3, 4, 5, 6].map((level) => (
                      <button
                        key={level}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
                          activeFormats.header === level
                            ? 'bg-gray-100 text-blue-600'
                            : ''
                        }`}
                        onClick={() => {
                          onFormatClick('header', level);
                          setShowHeadingDropdown(false);
                        }}
                      >
                        <span className="font-medium">H{level}</span>
                        <span className="text-gray-500">Heading {level}</span>
                      </button>
                    ))}
                    <button
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      onClick={() => {
                        onFormatClick('header', null);
                        setShowHeadingDropdown(false);
                      }}
                    >
                      Normal text
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Basic Formatting */}
            <div className="flex items-center gap-2  border-gray-200 pl-2">
              <button
                onClick={() => onFormatClick('bold')}
                className={getButtonClass('bold')}
                title="Bold"
              >
                <Bold size={18} />
              </button>
              <button
                onClick={() => onFormatClick('italic')}
                className={getButtonClass('italic')}
                title="Italic"
              >
                <Italic size={18} />
              </button>
              <button
                onClick={() => onFormatClick('underline')}
                className={getButtonClass('underline')}
                title="Underline"
              >
                <Underline size={18} />
              </button>
              <button
                onClick={() => onFormatClick('strike')}
                className={getButtonClass('strike')}
                title="Strikethrough"
              >
                <Strikethrough size={18} />
              </button>
            </div>

            {/* Text Colors */}
            <div className="flex items-center gap-2  border-gray-200 pl-2">
              <div className="relative" ref={colorRef}>
                <button
                  onClick={() => setShowColorDropdown(!showColorDropdown)}
                  className={getButtonClass('color')}
                  title="Text Color"
                >
                  <Palette size={18} />
                </button>
                {showColorDropdown && (
                  <div className="absolute z-50 bottom-full left-0 mb-1 w-40 bg-background-main rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="py-1">
                      {colors.map((color) => (
                        <button
                          key={color.value}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
                            activeFormats.color === color.value
                              ? 'bg-gray-100'
                              : ''
                          }`}
                          onClick={() => {
                            onFormatClick('color', color.value);
                            setShowColorDropdown(false);
                          }}
                        >
                          <div
                            className="w-4 h-4 rounded"
                            style={{
                              backgroundColor: color.value || 'transparent',
                              border: color.value ? 'none' : '1px solid #ccc',
                            }}
                          />
                          <span>{color.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" ref={bgColorRef}>
                <button
                  onClick={() => setShowBgColorDropdown(!showBgColorDropdown)}
                  className={getButtonClass('background')}
                  title="Background Color"
                >
                  <Highlighter size={18} />
                </button>
                {showBgColorDropdown && (
                  <div className="absolute z-50 bottom-full left-0 mb-1 w-40 bg-background-main rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="py-1">
                      {colors.map((color) => (
                        <button
                          key={color.value}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
                            activeFormats.background === color.value
                              ? 'bg-gray-100'
                              : ''
                          }`}
                          onClick={() => {
                            onFormatClick('background', color.value);
                            setShowBgColorDropdown(false);
                          }}
                        >
                          <div
                            className="w-4 h-4 rounded"
                            style={{
                              backgroundColor: color.value || 'transparent',
                              border: color.value ? 'none' : '1px solid #ccc',
                            }}
                          />
                          <span>{color.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Lists and Links */}
            <div className="flex items-center gap-2  border-gray-200 pl-2">
              <button
                onClick={() => onFormatClick('bullet')}
                className={getListButtonClass('list', 'bullet')}
                title="Bullet List"
              >
                <List size={18} />
              </button>
              <button
                onClick={() => onFormatClick('ordered')}
                className={getListButtonClass('list', 'ordered')}
                title="Numbered List"
              >
                <ListOrdered size={18} />
              </button>
            </div>

            {/* Insert Options */}
            <div className="flex items-center gap-2  border-gray-200 pl-2">
              <button
                onClick={() => onFormatClick('link')}
                className={getButtonClass('link')}
                title="Insert Link"
              >
                <LinkIcon size={18} />
              </button>
              <button
                onClick={() => onFormatClick('table')}
                className="p-2 hover:bg-gray-100 rounded"
                title="Insert Table"
              >
                <Table size={18} />
              </button>
            </div>
          </div>

          {/* Undo/Redo */}
          <div className="flex items-center gap-2  text-primary border-gray-200">
            <Button
              onClick={onUndo}
              variant="customSecondary"
              className="p-3 bg-secondary rounded"
              title="Undo"
            >
              <RotateCcw size={20} className="" />
            </Button>
            <Button
              variant="customSecondary"
              onClick={onRedo}
              className="p-3 bg-secondary rounded"
              title="Redo"
            >
              <RotateCw size={20} className="" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditToolbar;
